import { CheckCircle2 } from "lucide-react";
import posthog from "posthog-js";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

export interface AlreadyPublishedNotice {
  channelNames: string[];
  /** Latest publish time across the channels, or 0 if unknown. */
  publishedAt: number;
}

const WAYS_OUT = [
  { label: "Go to drafts", path: "/created", action: "drafts", variant: "outline" },
  { label: "Create a new post", path: "/generate", action: "generate", variant: "default" },
] as const;

/**
 * A publish link (an old OAuth return, a restored tab) opened a post that
 * already went out on every channel. Say so in the middle of the page and
 * offer somewhere useful to go; closing it stays in the editor.
 */
export function AlreadyPublishedDialog({
  notice,
  onStay,
  onLeave,
}: {
  notice: AlreadyPublishedNotice | null;
  onStay: () => void;
  onLeave: (path: string) => void;
}) {
  const where =
    notice?.channelNames.length === 1 ? notice.channelNames[0] : "all your channels";
  const when = notice?.publishedAt
    ? ` on ${new Date(notice.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    : "";
  return (
    <Dialog
      open={!!notice}
      onOpenChange={(open) => {
        if (open) return;
        posthog.capture("already_published_notice_action", { action: "stay" });
        onStay();
      }}
    >
      <DialogContent className="w-[calc(100%-2rem)] self-center rounded-lg sm:max-w-md">
        <DialogHeader className="items-center text-center sm:text-center">
          <span className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <DialogTitle>Already published</DialogTitle>
          <DialogDescription>
            This post went out to {where}
            {when}. There's nothing left to publish here.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 gap-2 sm:justify-center sm:space-x-0">
          {WAYS_OUT.map(({ label, path, action, variant }) => (
            <Button
              key={action}
              variant={variant}
              onClick={() => {
                posthog.capture("already_published_notice_action", { action });
                onLeave(path);
              }}
            >
              {label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
